import { z } from 'zod/v4';

export const AdminGetProductionProcessParamsSchema = z.object({
  id: z.string().min(1),
});

export type AdminGetProductionProcessParams = z.infer<
  typeof AdminGetProductionProcessParamsSchema
>;

/** Per-language overrides keyed by ISO 639-1 code, e.g. `{ de: { name: 'Färben' } }`. */
export const ProductionProcessTranslationsSchema = z.record(
  z.string(),
  z.object({
    name: z.string().optional(),
    description: z.string().optional(),
  }),
);

/**
 * The `data` payload of `GET /api/admin/production-processes/:id` — what the
 * legacy route's response definition let through. `translations` always
 * carries an `en` entry: the service falls back to `name` and `description`
 * when none is stored. `id` is an enum-like key (`FABRIC_PROCESSING`), not a
 * generated id.
 */
export const AdminProductionProcessSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  /** Hex fill for the icon's background. */
  color: z.string().nullable(),
  /** Hex fill for the glyph. */
  iconColor: z.string().nullable(),
  depthLevel: z.number().nullable(),
  translations: ProductionProcessTranslationsSchema,
  createdAt: z.coerce.date().nullable(),
  createdByUserId: z.string().nullable(),
  updatedAt: z.coerce.date().nullable(),
  updatedByUserId: z.string().nullable(),
  updatedByUserName: z.string().nullable(),
});

export type AdminProductionProcess = z.infer<
  typeof AdminProductionProcessSchema
>;

/**
 * One language's override as written. `name` is required; an empty
 * `description` is dropped rather than rejected — the old dashboard's
 * translations form sends one for every language left without a description,
 * which legacy's `minLength: 1` answered with a 400.
 */
const ProductionProcessTranslationInputSchema = z.object({
  name: z.string().min(1),
  description: z
    .string()
    .optional()
    .transform(description => description || undefined),
});

/**
 * The languages records can be translated into — the legacy `LANGUAGES` list.
 * Other keys are stripped, as legacy's ajv did; at least one must remain.
 */
export const ProductionProcessTranslationsInputSchema = z
  .object({
    en: ProductionProcessTranslationInputSchema.optional(),
    es: ProductionProcessTranslationInputSchema.optional(),
    de: ProductionProcessTranslationInputSchema.optional(),
    fr: ProductionProcessTranslationInputSchema.optional(),
  })
  .refine(t => Object.values(t).some(v => v !== undefined), {
    message: 'translations must contain at least one language',
  });

/**
 * The body of `PATCH /api/admin/production-processes/:id`. Every field is
 * optional and a field left out — or sent as `null` — keeps its stored value;
 * `translations`, when sent, replaces the stored set. `name` and `description`
 * are not writable here, as in legacy; they and any other key are stripped, so
 * the old dashboard can send the whole record back.
 *
 * `depthLevel` takes a number or a numeric string: frontend-v2 sends a string,
 * the old dashboard echoes the number it read.
 */
export const AdminUpdateProductionProcessBodySchema = z.object({
  color: z.string().min(1).nullish(),
  iconColor: z.string().min(1).nullish(),
  depthLevel: z
    .union([z.number().int().min(0), z.string().regex(/^\d+$/)])
    .nullish(),
  translations: ProductionProcessTranslationsInputSchema.nullish(),
});

export type AdminUpdateProductionProcessBody = z.infer<
  typeof AdminUpdateProductionProcessBodySchema
>;
